import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { GraduationCap } from "@phosphor-icons/react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ScrollProgress from "@/components/site/ScrollProgress";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { COURSES } from "./_partials/courses.data";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_integrated-matrix/artifacts/dpzplyzo_siIMr_wEQXtE6wi1joqDg.png";

function CourseCard({ course, featured }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.65 }}
      data-testid={`course-${course.id}`}
      className="group rounded-3xl overflow-hidden border-2 border-primary/30 bg-card
                 shadow-xl hover:border-primary
                 hover:shadow-[0_28px_70px_-18px_hsl(218_70%_28%/0.45)]
                 hover:-translate-y-1 transition-all"
    >
      <div className={`grid grid-cols-1 ${featured ? "md:grid-cols-12" : "md:grid-cols-12"} items-stretch`}>
        {/* Course image */}
        <div className={`relative aspect-video md:aspect-auto bg-foreground overflow-hidden ${featured ? "md:col-span-5" : "md:col-span-5"}`}>
          <img
            src={course.image}
            alt={`${course.title} — ${course.subtitle}`}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            loading="lazy"
            style={{ filter: "brightness(1.02) contrast(1.05) saturate(1.05)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-foreground/30 via-transparent to-transparent" />
        </div>

        {/* Body */}
        <div className={`p-7 md:p-9 flex flex-col ${featured ? "md:col-span-7" : "md:col-span-7"}`}>
          {course.audience && (
            <div className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-primary/75 mb-3">
              {course.audience}
            </div>
          )}
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-semibold leading-tight tracking-tight">
            {course.title}
          </h2>
          {course.subtitle && (
            <p className="mt-2 font-display text-lg md:text-xl text-foreground/70 leading-snug">
              {course.subtitle}
            </p>
          )}

          {course.badges && course.badges.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2">
              {course.badges.map((b) => (
                <span
                  key={b}
                  className="inline-flex items-center px-3 py-1 rounded-full
                             border border-primary/35 text-[0.65rem] uppercase
                             tracking-[0.2em] font-mono text-primary/85"
                >
                  {b}
                </span>
              ))}
            </div>
          )}

          {course.description && course.description.length > 0 && (
            <div className="mt-6 space-y-3">
              {course.description.map((p) => (
                <p
                  key={p.slice(0, 30)}
                  className="text-base text-foreground/80 leading-relaxed"
                >
                  {p}
                </p>
              ))}
            </div>
          )}

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <a
              href={course.purchaseUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`course-${course.id}-enroll`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full
                         bg-primary text-primary-foreground hover:bg-primary/90
                         font-semibold text-sm md:text-base
                         hover:gap-3 transition-all shadow-lg"
            >
              Enroll Now <span aria-hidden className="text-base">→</span>
            </a>
            {course.price && (
              <span
                data-testid={`course-${course.id}-price`}
                className="font-display text-lg md:text-xl font-semibold text-foreground"
              >
                {course.price}
              </span>
            )}
            <span className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-foreground/50">
              Hosted on Teachable
            </span>
          </div>
        </div>
      </div>
    </motion.article>
  );
}

export default function Academy() {
  useDocumentMeta({
    title: "Integrated Academy — Clinical Training & Provider Education",
    description:
      "Provider-focused clinical training built on the Integrated Wellness model — AI-guided care, hormone optimization, peptides, and whole-person wellness coaching.",
    image: HERO_IMG,
  });

  return (
    <div data-testid="academy-page" className="min-h-screen bg-background">
      <ScrollProgress />
      <Header />
      <main className="pt-32 md:pt-36 pb-20 md:pb-28">
        {/* Hero */}
        <section className="max-w-5xl mx-auto px-4 md:px-8 text-center">
          <Link
            to="/"
            data-testid="academy-back-link"
            className="eyebrow inline-flex items-center gap-2 text-foreground/65 hover:text-primary transition-colors"
          >
            <span aria-hidden>←</span> Back to Integrated Clinical Intelligence
          </Link>
          <div className="mt-8 inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/35 bg-card">
            <GraduationCap size={14} weight="duotone" className="text-primary" />
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.32em] text-primary">
              Integrated Academy
            </span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mt-6 font-display font-bold tracking-[-0.03em] leading-[0.98]
                       text-[clamp(2.5rem,6vw,5rem)]"
          >
            Train inside the <em className="not-italic text-primary">Unified Medical Model</em>.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-7 text-lg md:text-xl text-foreground/75 leading-relaxed max-w-2xl mx-auto"
          >
            Clinical training, provider education, and patient-coaching programs —
            built around the same framework that powers Integrated Wellness Medical Centers.
          </motion.p>
        </section>

        {/* Course list */}
        <section className="max-w-5xl mx-auto px-4 md:px-8 mt-16 md:mt-20 space-y-8 md:space-y-10">
          {COURSES.map((course, i) => (
            <CourseCard key={course.id} course={course} featured={i === 0} />
          ))}
        </section>

        {/* Closing note */}
        <section className="max-w-3xl mx-auto px-4 md:px-8 mt-20 md:mt-24 text-center">
          <p className="text-base md:text-lg text-foreground/65 leading-relaxed italic">
            More courses being added throughout 2026 — stay tuned.
          </p>
        </section>
      </main>
      <Footer />
    </div>
  );
}
